import * as z from 'zod';

export const GetAppInstanceRequest = z.object({});
export const GetAppInstanceResponse = z.object({
  instance: z
    .object({
      instanceId: z
        .string()
        .describe(
          "App instance ID. Useful to keep track of the\ndata that's associated with the specific instance of your app\ninstalled on a Wix site."
        )
        .optional(),
      appName: z
        .string()
        .describe('App name, as you entered it in the App Dashboard.')
        .optional(),
      appVersion: z
        .string()
        .describe("Version of your app that's installed on the Wix site.")
        .optional()
        .nullable(),
      isFree: z
        .boolean()
        .describe(
          'Whether the Wix user has installed a free or paid version of your app\non their site.'
        )
        .optional(),
      billing: z
        .object({
          packageName: z
            .string()
            .describe('Name of the package that the site owner has paid for.')
            .optional(),
          billingCycle: z
            .enum([
              'NO_CYCLE',
              'MONTHLY',
              'YEARLY',
              'ONE_TIME',
              'TWO_YEARS',
              'THREE_YEARS',
              'FOUR_YEARS',
              'FIVE_YEARS',
            ])
            .describe(
              'Interval of the billing cycle. `ONE_TIME` indicates that the Wix user has\nmade a single upfront payment without any automatic subscription renewal.\nThis is primarily for usage credits (for example, 5 SMS) but may occasionally apply\nto a one-time setup for the app.'
            )
            .optional(),
          timeStamp: z
            .string()
            .describe(
              "Date and time the Wix user purchased the app's paid plan or began their free trial. In\n`YYYY-MM-DDThh:mm:ss.sssZ` format."
            )
            .optional(),
          expirationDate: z
            .string()
            .describe(
              "Date and time the app's current billing cycle ends in\n`YYYY-MM-DDThh:mm:ss.sssZ` format. Available only for yearly and\nmulti-yearly plans."
            )
            .optional()
            .nullable(),
          autoRenewing: z
            .boolean()
            .describe(
              "Whether the app's subscription automatically renews at the end of the\ncurrent billing cycle."
            )
            .optional()
            .nullable(),
          invoiceId: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe('ID of the invoice for the current billing cycle.')
            .optional()
            .nullable(),
          source: z
            .string()
            .describe(
              "Information about any discounts applied to the app instance's current billing cycle.\nIf the site owners applied a developer coupon or Wix Voucher\nwhen installing the paid version of your app, this field holds the coupon's\nname or `“Wix discount coupon”`. Site owners may receive a Wix Voucher when\nupgrading their Wix subscription. If there is no discount for the\ncurrent billing cycle, the field is an empty string."
            )
            .optional()
            .nullable(),
          freeTrialInfo: z
            .object({
              status: z
                .enum(['IN_PROGRESS', 'ENDED', 'NOT_AVAILABLE'])
                .describe('Current free trial status.')
                .optional(),
              endDate: z
                .date()
                .describe(
                  'When the free trial has ended. Populated only once the free trial is over.'
                )
                .optional()
                .nullable(),
            })
            .describe('Information about the free trial applied, if relevant.')
            .optional(),
        })
        .describe(
          'Billing information for the app instance. Available only in case\n`{"isFree": false}`.'
        )
        .optional(),
      permissions: z.array(z.string()).optional(),
      availablePlans: z
        .array(
          z.object({
            packageName: z
              .string()
              .describe('Package name of the available plan.')
              .optional(),
            source: z
              .string()
              .describe(
                'Source of the available plan. Can be a bundle or 3rd-party app.'
              )
              .optional(),
          })
        )
        .optional(),
      originInstanceId: z
        .string()
        .describe(
          'ID of the Wix site from which the instance of your app has been cloned.\n\nAll visual settings of the Wix site and app data are duplicated during the\ncloning process. Wix also notifies you in case there is any additional\nexternal functionality for the original site.'
        )
        .optional()
        .nullable(),
      isOriginSiteTemplate: z
        .boolean()
        .describe(
          '__Deprecated__. This parameter will be removed on March 30, 2023. Use\n`copiedFromTemplate` instead.'
        )
        .optional(),
      copiedFromTemplate: z
        .boolean()
        .describe(
          'Whether this app instance was created when another Wix site was cloned.'
        )
        .optional(),
      freeTrialAvailable: z
        .boolean()
        .describe(
          "Whether this app instance includes a free trial that hasn't started yet."
        )
        .optional(),
    })
    .describe('Retrieved app instance.')
    .optional(),
  site: z
    .object({
      siteDisplayName: z
        .string()
        .describe('Display name of the site.')
        .optional()
        .nullable(),
      locale: z
        .string()
        .describe(
          "2-letter language code of the site's locale in\n[ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format."
        )
        .optional(),
      paymentCurrency: z
        .string()
        .describe(
          "3-letter currency code for the site's billing in\n[ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format."
        )
        .optional(),
      multilingual: z
        .object({
          isMultiLingual: z
            .boolean()
            .describe('Whether the site supports more than a single language.')
            .optional(),
          supportedLanguages: z
            .array(
              z.object({
                languageCode: z
                  .string()
                  .describe(
                    'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
                  )
                  .optional(),
                locale: z
                  .object({
                    languageCode: z
                      .string()
                      .describe(
                        'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
                      )
                      .optional(),
                    country: z
                      .string()
                      .describe(
                        'Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
                      )
                      .optional(),
                  })
                  .describe('Locale.')
                  .optional(),
                isPrimary: z
                  .boolean()
                  .describe(
                    'Whether the supported language is the primary language for the site.'
                  )
                  .optional(),
                countryCode: z.string().describe('Language icon.').optional(),
                resolutionMethod: z
                  .enum(['QUERY_PARAM', 'SUBDOMAIN', 'SUBDIRECTORY'])
                  .describe(
                    'How the language will be resolved. For internal use.'
                  )
                  .optional(),
                isVisitorPrimary: z
                  .boolean()
                  .describe(
                    'Whether the supported language is the primary language for site visitors.'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
        })
        .describe("Information about the site's supported languages.")
        .optional(),
      url: z
        .string()
        .describe(
          'URL of the site. Available only when the site has been published.'
        )
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Description of the site.')
        .optional()
        .nullable(),
      installedWixApps: z.array(z.string()).optional(),
      ownerEmail: z
        .string()
        .describe(
          '> **Deprecation Notice:** This parameter will be removed on June 30, 2022. Use `ownerInfo` instead.'
        )
        .email()
        .optional()
        .nullable(),
      ownerInfo: z
        .object({
          email: z
            .string()
            .describe(
              "Wix user's email address. Identical to the their login email."
            )
            .email()
            .optional(),
          emailStatus: z
            .string()
            .describe(
              'Supported values: `VERIFIED_OPT_IN`, `VERIFIED_OPT_OUT`,\n`NOT_VERIFIED_OPT_IN`, `NOT_VERIFIED_OPT_OUT`.\n\nWhether the Wix user has verified their email and whether they have chosen\nto receive email notifications from Wix.'
            )
            .optional(),
        })
        .describe(
          "Information about the site's Wix user. Available only when calling\n_Get App Instance_ with the __Read Site Owner Email__ permission scope."
        )
        .optional(),
      siteId: z.string().describe('Site ID.').optional(),
      siteUserType: z
        .string()
        .describe(
          'Type of Wix user who owns the site.\nAvailable only when calling _Get App Instance_ with the __Read Site User Type__ permission scope.\nSupported values: `"Channel DIY"`, `"Channel DIFM"`, `"Enterprise"`.'
        )
        .optional()
        .nullable(),
    })
    .describe('Information about the site.')
    .optional(),
});
